/**
 * InfographicEntryButton — small "Open infographic" pill dropped into
 * the CURRENT BOOK left-rail card on both /bodha and /sankalpa.
 *
 * Click-reliability (2026-02 real-user report, iter 2):
 *  - Real-user repro: 3 consecutive plain clicks on a fresh page load
 *    (no scroll, no cache) failed silently. Playwright's synthesized
 *    clicks always succeeded, which is why the previous fix's stress
 *    test showed 10/10 without exposing the actual issue.
 *  - Root cause (best current theory): the previous iteration used
 *    onPointerDown to call setOpen(true) synchronously. Radix Dialog
 *    mounts its Content + registers its `pointerDownOutside` detector
 *    during the same event bubble. On some browsers the very
 *    pointerdown that opened the dialog is caught by that detector as
 *    an "outside" event and immediately closes it — the dialog flashes
 *    open for a frame and then closes before the user sees anything.
 *    Additionally the ::before absolute hitbox pseudo without
 *    pointer-events:none can hit-test differently across engines.
 *  - Fix: use only onClick (fires after pointerup, by which time Radix
 *    is fully mounted and its detector correctly ignores the trigger
 *    event). Remove the ::before hitbox — it added risk without
 *    matching the user's actual complaint (a fresh, plain click, not
 *    a near-miss tap). Keep z-index + explicit pointer-events:auto so
 *    the button reliably wins the hit-test against any sibling.
 */
import React, { useState, useMemo } from "react";
import { LayoutDashboard } from "lucide-react";
import BookInfographicOverlay from "./BookInfographicOverlay";
import { T } from "./homev3/tokens";

export default function InfographicEntryButton({ book, lang = "en" }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);

  const snapshot = useMemo(() => {
    if (!book) return null;
    return {
      id:          String(book.id),
      title:       book.title || "",
      author:      book.author || "",
      summary:     book.summary || "",
      insights:    Array.isArray(book.insights)    ? book.insights    : [],
      actionItems: Array.isArray(book.actionItems) ? book.actionItems : [],
      coreTheme:   book.coreTheme || "",
    };
  }, [book]);

  if (!book) return null;

  return (
    <>
      <button
        type="button"
        data-testid="infographic-entry-button"
        onClick={() => setOpen(true)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          // Defensive stacking — the parent <aside> is position:sticky
          // which creates its own containing block; if any sibling ever
          // shares the button's y-band during a transient re-layout,
          // z-index:2 keeps the button on top.
          position: "relative",
          zIndex: 2,
          pointerEvents: "auto",
          // Visual pill (unchanged from original design)
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          width: "100%",
          padding: "10px 12px",
          fontFamily: T.F_HEADING,
          fontSize: 12,
          letterSpacing: "0.08em",
          color: T.DARK_BROWN,
          background: hover ? "rgba(201,168,76,0.15)" : "transparent",
          border: `1px solid ${T.GOLD}`,
          borderRadius: 999,
          cursor: "pointer",
          transition: "background 140ms ease",
          marginBottom: 10,
        }}
      >
        <LayoutDashboard size={12} strokeWidth={1.75} />
        Open infographic
      </button>

      <BookInfographicOverlay
        open={open}
        onOpenChange={setOpen}
        bookId={String(book.id)}
        bookSnapshot={snapshot}
        lang={lang}
      />
    </>
  );
}
